import { type FC, useState, useCallback, type SyntheticEvent, type ReactNode } from 'react';

import { Tab } from '@mui/base/Tab';
import { Tabs } from '@mui/base/Tabs';
import { TabsList } from '@mui/base/TabsList';
import { type MicroCopy } from '@vakantiesnl/types';
import cls from 'classnames';

import { useCustomStyles } from './TabsBar.style';

export type TabMenuItem = {
	id: number;
	name: string;
	children: ReactNode;
};

type TabPanelProps = {
	children: ReactNode;
	index: number;
	value: number;
	className?: string;
};

type A11yProps = { id: string; 'aria-controls': string };

function a11yProps(tabItem: TabMenuItem): A11yProps {
	return {
		id: `tab-${tabItem.name}`,
		'aria-controls': `tabpanel-${tabItem.name}`,
	};
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => (
	<div
		role="tabpanel"
		hidden={value !== index}
		id={`simple-tabpanel-${index}`}
		aria-labelledby={`simple-tab-${index}`}
	>
		{value === index && children}
	</div>
);

export type TabsBarProps = {
	/** Items to put in the tabs */
	menuItems: TabMenuItem[];
	/** Contentful microcopies */
	microCopies: MicroCopy;
	/** Prefix for tabs microcopy */
	microCopyPrefix: string;
	/** Callback onChange selected tab **/
	onTabChange?: (tabIndex: number, tabCaption?: string) => void;
	/** Cy element to test with */
	dataCy?: string;
};

export const TabsBar: FC<TabsBarProps> = ({ menuItems, microCopies, microCopyPrefix, onTabChange, dataCy }) => {
	const { classes } = useCustomStyles();

	const [value, setValue] = useState(0);

	const handleChange = useCallback(
		(_event: SyntheticEvent<Element, Event> | null, newValue: string | number | null) => {
			if (typeof newValue === 'number') {
				setValue(newValue);
				if (onTabChange) {
					onTabChange(
						newValue,
						menuItems[newValue] &&
							microCopies[`${microCopyPrefix.replace(/\s+/g, '')}${menuItems[newValue].name}`],
					);
				}
			}
		},
		[onTabChange, microCopies, microCopyPrefix, menuItems],
	);

	return (
		<Tabs value={value} onChange={handleChange} aria-label="Tabs" data-cy={dataCy}>
			<TabsList className={classes.tabsList}>
				{menuItems.map((item) => (
					<Tab
						className={cls(classes.tab, {
							[classes.selected]: value === item.id,
						})}
						key={item.name}
						data-cy={item.name}
						{...a11yProps(item)}
					>
						{microCopies[`${microCopyPrefix.replace(/\s+/g, '')}${item.name}`]}
					</Tab>
				))}
			</TabsList>

			{menuItems.map((item) => (
				<TabPanel key={item.name} value={value} index={item.id}>
					{item.children}
				</TabPanel>
			))}
		</Tabs>
	);
};
